import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { logger } from '../utils/logger'
import type {
  HealthStatus,
  Project,
  ProjectCreate,
  ProjectUpdate,
  ProjectPlotSettings,
  ProjectPlotSettingsUpdate,
  TextSelection,
  SelectionCreate,
  OCRResult,
  OCRBatchResult,
  AIFormatResult,
  AICoordinateResult
} from '@/types'

const DEBUG = ((import.meta as any).env?.VITE_DEBUG === 'true') || ((import.meta as any).env?.MODE !== 'production')

// Simple cache for API responses
class ApiCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>()
  
  get(key: string): any | null {
    const entry = this.cache.get(key)
    if (!entry) return null
    
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key)
      return null
    }
    
    return entry.data
  }
  
  set(key: string, data: any, ttl: number = 30000): void {
    this.cache.set(key, { data, timestamp: Date.now(), ttl })
  }
  
  clear(): void {
    this.cache.clear()
  }
}

// Request throttling utility
class RequestThrottler {
  private pendingRequests = new Map<string, Promise<any>>()
  
  async throttle<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    if (this.pendingRequests.has(key)) {
      logger.debug(`Throttling duplicate request: ${key}`)
      return this.pendingRequests.get(key) as Promise<T>
    }
    
    const promise = requestFn()
    this.pendingRequests.set(key, promise)
    
    try {
      const result = await promise
      return result
    } finally {
      this.pendingRequests.delete(key)
    }
  }
}

export class ApiService {
  private client: AxiosInstance
  private baseUrl: string
  private cache = new ApiCache()
  private throttler = new RequestThrottler()

  constructor(baseUrl?: string) {
    // Debug environment variable access
    const envApiUrl = (import.meta as any).env?.VITE_API_BASE_URL
    const envAuthUrl = (import.meta as any).env?.VITE_AUTH_BASE_URL
    
    if (DEBUG) {
      logger.debug('Environment variable debug:')
      logger.debug('  VITE_API_BASE_URL:', envApiUrl)
      logger.debug('  VITE_AUTH_BASE_URL:', envAuthUrl)
      logger.debug('  import.meta.env:', (import.meta as any).env)
      logger.debug('  baseUrl parameter:', baseUrl)
    }
    
    // SIMPLIFIED: Use environment variable or sensible defaults
    let determinedBaseUrl: string
    
    if (baseUrl) {
      // Explicitly passed baseUrl takes precedence
      determinedBaseUrl = baseUrl
    } else if (envApiUrl) {
      // Environment variable is set - this is the preferred method
      determinedBaseUrl = envApiUrl
    } else {
      // Simple fallback for local development only
      const currentHost = window.location.hostname
      if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
        determinedBaseUrl = 'http://localhost:8080/api'
      } else {
        // For all deployed environments, require VITE_API_BASE_URL to be set
        console.error('❌ VITE_API_BASE_URL not set! This should be configured in your deployment.')
        determinedBaseUrl = '/api' // Fallback to relative path
      }
    }
    
    this.baseUrl = determinedBaseUrl
    
    if (DEBUG) {
      console.log('🔍 API Service Configuration:', {
        envApiUrl,
        determinedBaseUrl,
        note: 'Auth is now handled by AuthService using Better Auth client directly'
      })
    }
    
    // Create main API client (for app data)
    this.client = axios.create({
      baseURL: this.baseUrl.startsWith('/') ? window.location.origin + this.baseUrl : this.baseUrl,
      timeout: 30000,
      withCredentials: true, // Send cookies for Better Auth session validation
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (DEBUG) {
      console.log('🔗 API Service initialized with base URL:', this.baseUrl)
    }
    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use((config) => {
      console.log('🔍 API Request:', {
        url: config.url,
        baseURL: config.baseURL,
        withCredentials: config.withCredentials,
        cookies: document.cookie,
        cookieDetails: this.parseCookies(document.cookie)
      })
      
      // Better Auth uses session cookies for authentication
      // No JWT tokens needed
      
      return config
    })
    
    this.client.interceptors.request.use(
      (config) => {
        // Better Auth uses session cookies for authentication
        
        if (DEBUG) {
          console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`)
          console.log(`🍪 Available cookies: ${document.cookie}`)
          console.log(`🔗 Request URL: ${config.baseURL}${config.url}`)
        }
        return config
      },
      (error) => {
        console.error('❌ API Request Error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        if (DEBUG) console.log(`✅ API Response: ${response.status} ${response.config.url}`)
        return response
      },
      (error) => {
        console.error('❌ API Response Error:', error.response?.data || error.message)
        
        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
          // Better Auth handles session expiry via cookies
          console.log('🔒 Authentication required')
        }
        
        return Promise.reject(error)
      }
    )
  }

  // Clear cache when needed
  clearCache(): void {
    this.cache.clear()
  }

  // Helper method to parse cookies for debugging
  private parseCookies(cookieString: string): Record<string, string> {
    const cookies: Record<string, string> = {}
    if (!cookieString) return cookies
    
    cookieString.split(';').forEach(cookie => {
      const [name, value] = cookie.trim().split('=')
      if (name && value) {
        cookies[name] = value
      }
    })
    return cookies
  }

  // Health endpoints
  async getHealth(): Promise<{ status: string }> {
    const healthUrl = `${this.baseUrl}/health`
    console.log('🔍 Health check URL:', healthUrl)
    const response = await fetch(healthUrl);
    console.log('🔍 Health check response status:', response.status)
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  // Note: Authentication is now handled by the AuthService using Better Auth client directly
  // These methods have been removed to avoid conflicts and ensure single source of truth

  async getProfile(): Promise<any> {
    const response = await this.client.get('/auth/me')
    return response.data
  }

  async updateProfile(userData: any): Promise<any> {
    const response = await this.client.patch('/auth/me', userData)
    return response.data
  }

  async changePassword(passwordData: { current_password: string; new_password: string }): Promise<void> {
    await this.client.post('/auth/change-password', passwordData)
  }

  async getDetailedHealth(): Promise<HealthStatus> {
    const response = await this.client.get<HealthStatus>('/health/detailed')
    return response.data
  }

  // Project endpoints with caching
  async listProjects(skip: number = 0, limit: number = 100): Promise<{ projects: Project[]; total: number }> {
    const cacheKey = `projects:${skip}:${limit}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached projects list')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get('/projects', {
        params: { skip, limit }
      })
      this.cache.set(cacheKey, response.data, 15000) // Cache for 15 seconds
      return response.data
    })
  }

  async createProject(projectData: ProjectCreate): Promise<Project> {
    const response = await this.client.post<Project>('/projects', projectData)
    this.cache.clear() // Clear cache after create
    return response.data
  }

  async getProject(projectId: string): Promise<Project> {
    const cacheKey = `project:${projectId}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached project data')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get<Project>(`/projects/${projectId}`)
      this.cache.set(cacheKey, response.data, 30000) // Cache for 30 seconds
      return response.data
    })
  }

  async updateProject(projectId: string, projectData: ProjectUpdate): Promise<Project> {
    const response = await this.client.put<Project>(`/projects/${projectId}`, projectData)
    this.cache.clear() // Clear cache after update
    return response.data
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.client.delete(`/projects/${projectId}`)
    this.cache.clear() // Clear cache after delete
  }

  async duplicateProject(projectId: string, projectData: ProjectCreate): Promise<Project> {
    const response = await this.client.post<Project>(`/projects/${projectId}/duplicate`, projectData)
    this.cache.clear() // Clear cache after duplicate
    return response.data
  }


  // New multi-document API methods
  async getProjectDocuments(projectId: string): Promise<{ project_id: string; project_name: string; documents: any[]; document_count: number }> {
    const cacheKey = `project-documents:${projectId}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached project documents')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get(`/documents/project/${projectId}`)
      this.cache.set(cacheKey, response.data, 20000) // Cache for 20 seconds
      return response.data
    })
  }

  async createManualTextDocument(projectId: string, textContent: string, filename: string): Promise<any> {
    const formData = new FormData()
    formData.append('project_id', projectId)
    formData.append('text_content', textContent)
    formData.append('filename', filename)

    const response = await this.client.post('/documents/create-manual', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    this.cache.clear() // Clear cache after creating new document
    return response.data
  }

  async updateManualTextDocument(documentId: string, textContent: string): Promise<any> {
    const formData = new FormData()
    formData.append('text_content', textContent)

    const response = await this.client.put(`/documents/${documentId}/update-text`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    this.cache.clear() // Clear cache after updating document
    return response.data
  }

  async getDocumentInfo(documentId: string): Promise<any> {
    const cacheKey = `document-info:${documentId}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached document info')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get(`/documents/${documentId}`)
      this.cache.set(cacheKey, response.data, 30000) // Cache for 30 seconds
      return response.data
    })
  }

  async downloadDocument(documentId: string): Promise<string> {
    const response = await this.client.get(`/documents/${documentId}/download`, {
      responseType: 'text'
    })
    return response.data
  }

  async downloadDocumentAsBlob(documentId: string): Promise<Blob> {
    const response = await this.client.get(`/documents/${documentId}/download`, {
      responseType: 'blob'
    })
    return response.data
  }

  async downloadDocumentAsArrayBuffer(documentId: string): Promise<ArrayBuffer> {
    const response = await this.client.get(`/documents/${documentId}/download`, {
      responseType: 'arraybuffer'
    })
    return response.data
  }

  async deletePDF(fileId: string): Promise<{ message: string }> {
    const result = await this.deleteUpload(fileId)
    this.cache.clear() // Clear cache after delete
    return result
  }

  async getProjectPlotSettings(projectId: string): Promise<ProjectPlotSettings> {
    const cacheKey = `plot-settings:${projectId}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached plot settings')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get<ProjectPlotSettings>(`/projects/${projectId}/plot-settings`)
      this.cache.set(cacheKey, response.data, 60000) // Cache for 60 seconds
      return response.data
    })
  }

  async updateProjectPlotSettings(projectId: string, settings: ProjectPlotSettingsUpdate): Promise<ProjectPlotSettings> {
    const response = await this.client.put<ProjectPlotSettings>(`/projects/${projectId}/plot-settings`, settings)
    this.cache.clear() // Clear cache after update
    return response.data
  }

  async getProjectWorkflowData(projectId: string): Promise<{ numbered_list_text?: string; metes_bounds_text?: string }> {
    const cacheKey = `workflow-data:${projectId}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached workflow data')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get(`/projects/${projectId}/workflow-data`)
      this.cache.set(cacheKey, response.data, 30000) // Cache for 30 seconds
      return response.data
    })
  }

  async updateProjectWorkflowData(projectId: string, data: { numbered_list_text?: string; metes_bounds_text?: string }): Promise<{ numbered_list_text?: string; metes_bounds_text?: string }> {
    const response = await this.client.put(`/projects/${projectId}/workflow-data`, data)
    this.cache.clear() // Clear cache after update
    return response.data
  }

  // Selection endpoints with caching and throttling
  async listProjectSelections(projectId: string, skip: number = 0, limit: number = 100): Promise<{ selections: TextSelection[]; total: number }> {
    const cacheKey = `selections:${projectId}:${skip}:${limit}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached project selections')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get(`/selections/project/${projectId}`, {
        params: { skip, limit }
      })
      this.cache.set(cacheKey, response.data, 10000) // Cache for 10 seconds
      return response.data
    })
  }

  async listPDFSelections(pdfId: string, skip: number = 0, limit: number = 100): Promise<{ selections: TextSelection[]; total: number }> {
    const cacheKey = `pdf-selections:${pdfId}:${skip}:${limit}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached PDF selections')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get(`/selections/pdf/${pdfId}`, {
        params: { skip, limit }
      })
      this.cache.set(cacheKey, response.data, 10000) // Cache for 10 seconds
      return response.data
    })
  }

  async createSelection(selectionData: SelectionCreate): Promise<TextSelection> {
    // Create a clean object with only SelectionCreate fields to prevent extra fields
    const cleanData: SelectionCreate = {
      project_id: selectionData.project_id,
      document_id: selectionData.document_id,
      document_type: selectionData.document_type,
      x: selectionData.x,
      y: selectionData.y,
      width: selectionData.width,
      height: selectionData.height,
      page_number: selectionData.page_number,
      thumbnail_data: selectionData.thumbnail_data
    }
    
    // Debug: Log the exact data being sent
    console.log('🔍 createSelection - Sending data:', JSON.stringify(cleanData, null, 2))
    console.log('🔍 createSelection - Data keys:', Object.keys(cleanData))
    
    const response = await this.client.post<TextSelection>('/selections', cleanData)
    this.cache.clear() // Clear cache after create
    return response.data
  }

  async getSelection(selectionId: string): Promise<TextSelection> {
    const cacheKey = `selection:${selectionId}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached selection')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get<TextSelection>(`/selections/${selectionId}`)
      this.cache.set(cacheKey, response.data, 30000) // Cache for 30 seconds
      return response.data
    })
  }

  async updateSelection(selectionId: string, updates: Partial<TextSelection>): Promise<TextSelection> {
    const response = await this.client.put<TextSelection>(`/selections/${selectionId}`, updates)
    this.cache.clear() // Clear cache after update
    return response.data
  }

  // ============================================================================
  // OCR Background Processing Methods
  // ============================================================================

  async startOCRProcessing(selectionId: string): Promise<{ status: string; message?: string }> {
    const response = await this.client.post(`/selections/${selectionId}/ocr/start`)
    return response.data
  }

  async getOCRStatus(selectionId: string): Promise<{
    selection_id: string;
    ocr_status: string;
    extracted_text?: string;
    ocr_confidence?: number;
    started_at?: string;
    completed_at?: string;
    error?: string;
    processed: string;
  }> {
    const response = await this.client.get(`/selections/${selectionId}/ocr/status`)
    return response.data
  }

  async retryOCRProcessing(selectionId: string): Promise<{ status: string; message?: string }> {
    const response = await this.client.post(`/selections/${selectionId}/ocr/retry`)
    return response.data
  }

  async getOCRMetrics(): Promise<{
    metrics: any;
    active_tasks: string[];
    active_task_count: number;
    timestamp: string;
  }> {
    const response = await this.client.get('/selections/ocr/metrics')
    return response.data
  }

  async deleteSelection(selectionId: string): Promise<void> {
    await this.client.delete(`/selections/${selectionId}`)
    this.cache.clear() // Clear cache after delete
  }

  async deleteAllProjectSelections(projectId: string): Promise<void> {
    await this.client.delete(`/selections/project/${projectId}/all`)
    this.cache.clear() // Clear cache after delete
  }

  // Upload endpoints
  async uploadDocument(file: File, projectId: string): Promise<{ file_id: string; project_id: string; project_name: string; original_filename: string; file_size: number; upload_timestamp: string }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('project_id', projectId)

    const response = await this.client.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    this.cache.clear() // Clear cache after upload
    
    // Transform new API response format to match expected format
    const data = response.data
    return {
      file_id: data.document_id,  // Map document_id to file_id
      project_id: data.project_id,
      project_name: '',  // New API doesn't return project_name
      original_filename: data.original_filename,
      file_size: data.file_size,
      upload_timestamp: data.upload_timestamp
    }
  }


  async listUploads(): Promise<{ uploads: any[] }> {
    const cacheKey = 'uploads'
    const cached = this.cache.get(cacheKey)
    if (cached) {
      console.log('📋 Using cached uploads')
      return cached
    }

    return this.throttler.throttle(cacheKey, async () => {
      const response = await this.client.get('/uploads')
      this.cache.set(cacheKey, response.data, 15000) // Cache for 15 seconds
      return response.data
    })
  }

  async deleteUpload(fileId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/upload/${fileId}`)
    this.cache.clear() // Clear cache after delete
    return response.data
  }

  // Document endpoints (duplicates removed - using cached versions above)

  // OCR endpoints
  async processOCR(imageData: string, language: string = 'eng', confidenceThreshold: number = 0.0): Promise<OCRResult> {
    const response = await this.client.post<OCRResult>('/ocr/process', {
      image_data: imageData,
      language,
      confidence_threshold: confidenceThreshold,
    })
    return response.data
  }

  // 🚀 PERFORMANCE OPTIMIZATION: Fast OCR method for quick processing
  async processFastOCR(imageData: string, language: string = 'eng'): Promise<OCRResult> {
    const response = await this.client.post<OCRResult>('/ocr/fast', {
      image_data: imageData,
      language,
      confidence_threshold: 0.0, // Not used in fast mode
    })
    return response.data
  }

  async processBatchOCR(images: string[], language: string = 'eng', confidenceThreshold: number = 0.0): Promise<OCRBatchResult> {
    const response = await this.client.post<OCRBatchResult>('/ocr/batch', {
      images,
      language,
      confidence_threshold: confidenceThreshold,
    })
    return response.data
  }

  async getOCRLanguages(): Promise<{ available_languages: string[]; default_language: string; tesseract_version: string }> {
    const response = await this.client.get('/ocr/languages')
    return response.data
  }

  async testOCR(): Promise<{ status: string; extracted_text: string; message: string; tesseract_version: string }> {
    const response = await this.client.post('/ocr/test')
    return response.data
  }

  // Text formatting endpoints
  async formatText(textSnippets: string[], instruction?: string): Promise<AIFormatResult> {
    const response = await this.client.post<AIFormatResult>('/ai/format-text', {
      text_snippets: textSnippets,
      instruction: instruction || 'Clean and format the text for data extraction, preserving numerical values and units.',
    })
    return response.data
  }

  async formatSelectionText(selectionId: string): Promise<TextSelection> {
    const response = await this.client.post<TextSelection>(`/selections/${selectionId}/format`)
    this.cache.clear() // Clear cache after format
    return response.data
  }

  async formatBatchSelections(selectionIds: string[]): Promise<{ formatted: TextSelection[]; failed: string[] }> {
    const response = await this.client.post<{ formatted: TextSelection[]; failed: string[] }>('/selections/format/batch', {
      selection_ids: selectionIds
    })
    this.cache.clear() // Clear cache after batch format
    return response.data
  }

  // AI endpoints
  async convertToCoordinates(descriptionList: string[], coordinateFormat?: string): Promise<AICoordinateResult> {
    const response = await this.client.post<AICoordinateResult>('/ai/convert-coordinates', {
      description_list: descriptionList,
      coordinate_format: coordinateFormat,
    })
    return response.data
  }

  async customAIProcessing(
    textInput: string,
    systemPrompt: string,
    userPrompt: string,
    model: string = 'gpt-4',
    temperature: number = 0.0,
    maxTokens: number = 4096
  ): Promise<{ result: string; processing_timestamp: string; model_used: string; tokens_used: number; temperature: number }> {
    const response = await this.client.post('/ai/custom-prompt', {
      text_input: textInput,
      system_prompt: systemPrompt,
      user_prompt: userPrompt,
      model,
      temperature,
      max_tokens: maxTokens,
    })
    return response.data
  }

  async getAIStatus(): Promise<{ openai_configured: boolean; available_endpoints: string[]; status: string }> {
    const response = await this.client.get('/ai/status')
    return response.data
  }

  // Note: Better Auth methods removed - use AuthService instead for all authentication

  // Note: Authentication is now handled by Better Auth session cookies
  // No JWT token management needed

  // Utility methods
  getPDFPageUrl(fileId: string, pageNum: number, scale: number = 1.5): string {
    return `${this.baseUrl}/pdf/${fileId}/page/${pageNum}?scale=${scale}`
  }

  async downloadFile(url: string, filename: string): Promise<void> {
    const response = await this.client.get(url, { responseType: 'blob' })
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)
  }
} 

// Export a singleton instance for convenience
export const apiService = new ApiService()